import { collectOrgIdsForAnyOrgPermission, requireAnyOrgPermission, requireAnySystemPermission, requireOrgPermission, } from '@juvare/contact-management/backend/features/permissions/shared';
import isEqual from 'lodash/isEqual';
import { badgeEntityName } from '../../entities/BadgeEntityTypes';
export function requireManageBadgesPermission(userState) {
    return requireAnySystemPermission(userState, 'manage-badges') || requireAnyOrgPermission(userState, 'manage-badges');
}
export function hasPermissionsToCreateBadge(userState) {
    const userStateCopy = adjustUserStateBadgingSpecific(userState);
    return requireAnySystemPermission(userStateCopy, 'manage-badges') || requireAnyOrgPermission(userStateCopy, 'manage-badges');
}
export function hasPermissionsToCreateAccountBadge(userState) {
    const userStateCopy = adjustUserStateBadgingSpecific(userState);
    return (requireAnySystemPermission(userStateCopy, 'manage-badges', 'manage-account-badges') ||
        requireAnyOrgPermission(userStateCopy, 'manage-badges', 'manage-account-badges'));
}
export function hasPermissionsToUpdateAccountBadge(userState, params) {
    const userStateCopy = adjustUserStateBadgingSpecific(userState);
    const isRevoked = params.data.status === 'revoked' && params.prevData.status !== 'revoked';
    if (isRevoked) {
        if (requireBaseBadgesPermissions(userStateCopy) && requireRevokeBadgesPermissions(userStateCopy)) {
            return true;
        }
        if (isRevokeWithoutAnyOtherChanges(params) &&
            (requireRevokeBadgesPermissions(userStateCopy) || userStateCopy.id === params.data.accountId)) {
            return true;
        }
        return false;
    }
    return requireBaseBadgesPermissions(userStateCopy);
}
export function hasPermissionToViewAccountBadges(userState, accountBadgesRequested) {
    const accountBadgesReq = accountBadgesRequested !== null && accountBadgesRequested !== void 0 ? accountBadgesRequested : [];
    const usersOwnAccountBadges = accountBadgesReq.filter((acc) => acc.columns.accountId === userState.id);
    const isRequestingOwnBadges = usersOwnAccountBadges.length === accountBadgesReq.length;
    const hasBasePermissionToProceed = requireAnySystemPermission(userState, 'manage-badges', 'manage-account-badges') ||
        // TODO should check individual accountBadge access - implement with PFBAD-268
        requireAnyOrgPermission(userState, 'manage-badges', 'manage-account-badges') ||
        isRequestingOwnBadges;
    if (!hasBasePermissionToProceed) {
        return { hasPermissionForSome: false, hasPermissionForAll: false, accountBadgesAllowed: [] };
    }
    if (requireAnySystemPermission(userState, 'manage-accounts')) {
        return { hasPermissionForSome: true, hasPermissionForAll: true, accountBadgesAllowed: accountBadgesRequested !== null && accountBadgesRequested !== void 0 ? accountBadgesRequested : [] };
    }
    // TODO should check individual accountBadge access - implement with PFBAD-268
    if (requireAnyOrgPermission(userState, 'manage-members')) {
        return {
            hasPermissionForSome: true,
            hasPermissionForAll: true,
            accountBadgesAllowed: accountBadgesRequested !== null && accountBadgesRequested !== void 0 ? accountBadgesRequested : [],
        };
    }
    if (usersOwnAccountBadges.length > 0) {
        return {
            hasPermissionForSome: true,
            hasPermissionForAll: usersOwnAccountBadges.length === accountBadgesReq.length,
            accountBadgesAllowed: usersOwnAccountBadges,
        };
    }
    return {
        hasPermissionForSome: false,
        hasPermissionForAll: false,
        accountBadgesAllowed: [],
    };
}
export function requireBaseBadgesPermissions(userState) {
    return (requireAnySystemPermission(userState, 'manage-badges', 'manage-account-badges') ||
        requireAnyOrgPermission(userState, 'manage-badges', 'manage-account-badges'));
}
export function requireManageMembersOrgPermission(userState, orgIdArray) {
    const hasAllPermissions = requireAnySystemPermission(userState, 'all-organizations-admin');
    const hasSpecifiedOrgPermissions = orgIdArray.every((orgId) => requireOrgPermission(userState, orgId, 'manage-members'));
    return hasAllPermissions || hasSpecifiedOrgPermissions;
}
export function requireManageBadgesOrgPermission(userState, orgIdArray) {
    const hasAllPermissions = requireAnySystemPermission(userState, 'all-organizations-admin');
    const hasSpecifiedOrgPermissions = orgIdArray.length > 0 ? orgIdArray.every((orgId) => requireOrgPermission(userState, orgId, 'manage-badges')) : false;
    return hasAllPermissions || hasSpecifiedOrgPermissions;
}
export function requireManageMemberPermission(userState, orgIdArray) {
    const hasSystemPermission = requireAnySystemPermission(userState, 'manage-accounts');
    const hasPermissionsToAnyOrg = orgIdArray.some((orgId) => requireOrgPermission(userState, orgId, 'manage-members')) && orgIdArray.length > 0;
    return hasSystemPermission || hasPermissionsToAnyOrg;
}
export function requireRevokeBadgesPermissions(userState) {
    return requireAnySystemPermission(userState, 'revoke-account-badges') || requireAnyOrgPermission(userState, 'revoke-account-badges');
}
export function requirePermissionToManageBadge(context, badgeIdOrRow) {
    var _a;
    if (!requireBaseBadgesPermissions(context.userState)) {
        return false;
    }
    const organizationIdsWhereAccountIsAdmin = collectOrgIdsForAnyOrgPermission(context.userState, 'manage-members');
    const hasManageOrganizationsPermission = requireAnySystemPermission(context.userState, 'all-organizations-admin', 'manage-organizations');
    const badgeStorage = context.storage(badgeEntityName);
    const badge = typeof badgeIdOrRow === 'string' ? badgeStorage.get(badgeIdOrRow) : badgeIdOrRow;
    const hasManageOrganizationPermission = organizationIdsWhereAccountIsAdmin.some((orgId) => badge.columns.organizationIds && badge.columns.organizationIds.includes(orgId));
    return hasManageOrganizationsPermission || ((_a = badge.columns.organizationIds) === null || _a === void 0 ? void 0 : _a.length) === 0 || hasManageOrganizationPermission;
}
/**
 * Clone UserState and remove some permissions:
 *
 * * `all-organizations-admin` - it allows client-admin to fit requirements
 */
function adjustUserStateBadgingSpecific(userState) {
    var _a;
    return Object.assign(Object.assign({}, userState), { systemPermissions: ((_a = userState.systemPermissions) !== null && _a !== void 0 ? _a : []).filter((p) => p !== 'all-organizations-admin') });
}
function isRevokeWithoutAnyOtherChanges(params) {
    if (params.data.status !== 'revoked') {
        return false;
    }
    return Object.keys(params.data).every((key) => (key === 'status' && params.data.status !== params.prevData.status) || // check if only status & statusReason are changed
        key === 'statusReason' ||
        (key !== 'status' && isEqual(params.data[key], params.prevData[key])));
}
